import { useState, useEffect, useCallback } from "react";
import { Loader2, ArrowRight, ArrowLeft, Reply, Mail } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function ContactEmailHistory({ contactId }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/email/conversation/contact/${contactId}?limit=100`);
      setEmails(data || []);
    } catch (e) {
      console.error("Email history load failed:", e);
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Chargement…
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="text-center py-10 text-sm text-muted-foreground">
        <Mail className="h-10 w-10 mx-auto mb-2 opacity-30" />
        Aucun email échangé avec ce contact pour l'instant.
        <div className="text-xs mt-2">
          Les emails envoyés depuis Outlook seront synchronisés automatiquement toutes les 10 min
          (si Microsoft 365 est connecté).
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1" data-testid="contact-email-history">
      {emails.map((e) => {
        const inbound = e.direction === "inbound";
        const dateLabel = e.sent_at || e.received_at || e.created_at;
        return (
          <div
            key={e.id}
            className={`border rounded p-3 ${inbound ? "bg-blue-50/30 border-blue-200" : "bg-muted/20"}`}
            data-testid={`email-row-${e.id}`}
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2 min-w-0">
                <Badge variant={inbound ? "default" : "outline"} className="text-[10px] shrink-0">
                  {inbound ? (
                    <><ArrowLeft className="h-3 w-3 mr-1" /> Reçu</>
                  ) : (
                    <><ArrowRight className="h-3 w-3 mr-1" /> Envoyé</>
                  )}
                </Badge>
                {e.replied && !inbound && (
                  <Badge variant="outline" className="text-[10px] text-green-700 border-green-700">
                    <Reply className="h-3 w-3 mr-1" /> Répondu
                  </Badge>
                )}
                {e.source === "m365_graph" && (
                  <Badge variant="outline" className="text-[10px]">M365</Badge>
                )}
              </div>
              <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                {fmtDateTime(dateLabel)}
              </span>
            </div>
            <div className="font-medium text-sm mb-1 truncate" title={e.subject}>
              {e.subject || "(sans objet)"}
            </div>
            {e.body_preview && (
              <div className="text-xs text-muted-foreground line-clamp-2">
                {e.body_preview}
              </div>
            )}
            <div className="text-[10px] text-muted-foreground mt-1 truncate">
              {inbound ? (
                <>De : <span className="font-mono">{e.from_email}</span></>
              ) : (
                <>À : <span className="font-mono">{e.to_email}</span></>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
